import { ENDPOINTS, TASK_TYPES } from 'features/daily-plan/constants';
import { JOB_ORDER_TAB_IDS } from 'features/inventory-profiles/constants';
import {
  candidateColumns,
  candidateVirtualProps,
  CustomCandidateRenders
} from 'UI/components/organisms/inventoryProfiles/CandidatesTab/columns';
import {
  HiringAuthorityVirtualProps,
  jobOrderProfileHAColumns
} from 'UI/components/organisms/inventoryProfiles/HiringAuthorityTab/jobOrderProfileHAColumns';
import {
  JOB_ORDER_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE,
  JOB_ORDER_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE
} from 'UI/components/organisms/inventoryProfiles/MatchMakingTab';
import {
  IncludesByMode,
  jobOrderProfilePlacementsColumns
} from 'UI/components/organisms/inventoryProfiles/PlacementsTab/columns';
import { handleHiringAuthorityResponse } from 'UI/components/organisms/inventoryProfiles/responseChange';
import {
  SENDOUTS_COLUMNS,
  SENDOUTS_RENDERERS,
  SENDOUTS_VIRTUAL_PROPS
} from 'UI/components/organisms/inventoryProfiles/SendoutsTab/columns';
import { ProfileTablesPreferenceKeys } from 'UI/components/organisms/inventoryProfiles/tabKeys';
import {
  formatTasksResponse,
  TASK_COLUMNS,
  TASKS_EXTRA_RENDERERS
} from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { Endpoints } from 'UI/constants/endpoints';
import {
  ApplicantEntity,
  CandidateEntity,
  ContactEntity,
  ContactRole,
  ENTITY_TYPE_ID_MAP,
  EntityType
} from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { hasFeatureFlag } from 'UI/utils';

const MATCH_MAKING_ENTITY_TYPES = [
  {
    columns: JOB_ORDER_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Candidate],
    endpoint: Endpoints.JobOrderMatchCandidate,
    formatResponse: JOB_ORDER_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Candidate],
    id: ENTITY_TYPE_ID_MAP.candidate,
    isEnabled: hasFeatureFlag(FeatureFlags.JobOrderMatchMakingByCandidate),
    secondaryEnpoint: Endpoints.JobOrderAIMatchCandidate,
    singular: CandidateEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersMatchCandidate,
    title: CandidateEntity.title,
    type: EntityType.Candidate
  },
  {
    columns: JOB_ORDER_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Applicant],
    disableTableActions: true,
    formatResponse: JOB_ORDER_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Applicant],
    id: ENTITY_TYPE_ID_MAP.applicant,
    isEnabled: hasFeatureFlag(FeatureFlags.JobOrderMatchMakingByApplicant),
    secondaryEnpoint: Endpoints.JobOrderAIMatchApplicants,
    singular: ApplicantEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersMatchApplicant,
    title: ApplicantEntity.title,
    type: EntityType.Applicant
  },
  {
    columns: JOB_ORDER_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Contact],
    formatResponse: JOB_ORDER_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Contact],
    id: ContactRole.Name,
    isEnabled: hasFeatureFlag(FeatureFlags.JobOrderMatchMakingByContact),
    matchedParamKey: 'matchedJobOrder',
    secondaryEnpoint: Endpoints.JobOrderAIMatchContacts,
    singular: ContactEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersMatchContact,
    title: ContactEntity.title,
    type: EntityType.Contact
  }
];

const ENABLED_MATCH_MAKING_ENTITY_TYPES = MATCH_MAKING_ENTITY_TYPES.filter(
  entity => entity.isEnabled
);

const SENDOUTS_INCLUDES =
  'type,status,interviews,hiringAuthorithies,jobOrderAccountable,candidateAccountable,joborder,candidate,jobOrderAccountableCoach,candidateAccountableCoach';

const SENDOUTS_TAB_CONFIG = {
  endpoint: Endpoints.Sendouts,
  tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersSendouts,
  extraRenderers: SENDOUTS_RENDERERS,
  initialColumns: SENDOUTS_COLUMNS,
  DataTableProps: {
    apiVersion: 2,
    virtualProps: SENDOUTS_VIRTUAL_PROPS,
    includes: SENDOUTS_INCLUDES
  }
};

export const JOB_ORDER_TABS_CONFIG = {
  [JOB_ORDER_TAB_IDS.MatchMaking]: {
    availableEntityTypesToMatch: ENABLED_MATCH_MAKING_ENTITY_TYPES,
    DataTableProps: { apiVersion: 1 }
  },
  [JOB_ORDER_TAB_IDS.Sendouts]: SENDOUTS_TAB_CONFIG,
  [JOB_ORDER_TAB_IDS.SendoutsV2]: SENDOUTS_TAB_CONFIG,
  [JOB_ORDER_TAB_IDS.Placements]: {
    endpoint: Endpoints.Placements,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersPlacements,
    initialColumns: jobOrderProfilePlacementsColumns,
    DataTableProps: {
      includes: IncludesByMode.joborder,
      apiVersion: 2
    }
  },
  [JOB_ORDER_TAB_IDS.Candidates]: {
    endpoint: Endpoints.Candidates,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersCandidate,
    initialColumns: candidateColumns,
    extraRenderers: CustomCandidateRenders,
    DataTableProps: {
      includes: 'personalInformation,specialty,blueSheets,lastActivityLog,keywordTags',
      virtualProps: candidateVirtualProps
    }
  },
  [JOB_ORDER_TAB_IDS.HiringAuthorities]: {
    endpoint: Endpoints.HiringAuthorities,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrdersHiringAuthority,
    initialColumns: jobOrderProfileHAColumns,
    DataTableProps: {
      virtualProps: HiringAuthorityVirtualProps,
      apiVersion: 1,
      includes: 'lastActivityLog,specialty,subspecialty,position,phones,status,subStatus',
      formatResponse: handleHiringAuthorityResponse
    }
  },
  [JOB_ORDER_TAB_IDS.ActionPlan]: {
    allowedTaskTypes: [TASK_TYPES.recruitJobOrder],
    DataTableProps: {
      apiVersion: 1,
      endpoint: ENDPOINTS.tasks,
      formatResponse: formatTasksResponse
    },
    entityType: EntityType.Joborder,
    entityTypeId: ENTITY_TYPE_ID_MAP.jobOrder,
    extraRenderers: TASKS_EXTRA_RENDERERS,
    initialColumns: TASK_COLUMNS,
    tablePreferencesKey: ProfileTablesPreferenceKeys.JobOrderTasks
  }
};
